/**
 * Words on the sign-in, reset and password screens. Kept together so Urdu can replace
 * them in Phase 2 (CLAUDE.md "Copy"). Functions take the names they need.
 */
export const authCopy = {
  brandLine: 'Your gym, in one place.',
  brandDetail: 'Members, fees, plans and messages, for owners, admins and trainers.',
  members: 'Members: sign in on the Fitnatic app on your phone.',
  /** Shown under the product name when no gym is known yet (the sign-in page). */
  byline: 'by WPCodie',

  signIn: {
    title: 'Sign in to your gym',
    subtitle: 'For owners, admins and trainers.',
    email: 'Email',
    emailPlaceholder: 'kamran@ironpulse.pk',
    emailRequired: 'Enter your email address',
    emailInvalid: 'Enter a valid email address, like name@gym.pk',
    password: 'Password',
    passwordRequired: 'Enter your password',
    submit: 'Sign in',
    submitting: 'Signing in',
    forgot: 'Forgot password?',
    wrong: 'Email or password is wrong.',
  },

  forgot: {
    title: 'Reset your password',
    subtitle: "Enter the email you sign in with. We'll email you a link to choose a new password.",
    submit: 'Email me a reset link',
    submitting: 'Sending the link',
    back: 'Back to sign in',
    phoneOnly: 'That address belongs to a member who signs in with their mobile number. Ask your gym for a new link instead.',
    sentTitle: 'Check your email',
    sent: (email: string) =>
      `If ${email} has a staff account, a link to choose a new password is on its way. The link works once and only for a short time. Check your spam folder too.`,
    noEmail: "No email? Ask your gym's owner to check the address on your account.",
  },

  recovery: {
    checking: 'Opening your reset link',
    expiredTitle: 'This link has expired',
    expired: 'Reset links work once and only for a short time. Ask for a new one.',
    again: 'Email me a new link',
    title: 'Choose a new password',
    subtitle: 'You opened a reset link. Pick a new password to sign in.',
    done: 'Password changed. Welcome back.',
    checkingInvite: 'Opening your trial gym',
    inviteTitle: 'Welcome to Fitnatic',
    inviteSubtitle: 'Your 15-day trial gym is ready. Choose a password, then you are in.',
    checkingReturning: 'Opening your gym',
    returningTitle: 'Welcome back to Fitnatic',
    returningSubtitle:
      'This email already has a Fitnatic gym, so we did not make a new one. Choose a password and you are in.',
  },

  /** A gym whose free trial ended: every account is locked until WPCodie re-opens it. */
  trialEnded:
    "This gym's 15-day free trial has ended, so sign-in is closed. To keep using Fitnatic, with everything still in it, email WPCodie at service@wpcodie.com.",

  forced: {
    title: (firstName: string) => `Choose your own password, ${firstName}`,
    subtitle: (gymName: string) =>
      `${gymName} gave you a temporary password. Pick a new one to continue.`,
    reassure: "You'll use this from now on. Nobody at the gym can see it.",
    signOut: 'Not you? Sign out',
    done: (gymName: string) => `Password saved. Welcome to ${gymName}.`,
  },

  newPassword: {
    current: 'Current password',
    currentRequired: 'Enter your current password',
    currentWrong: 'Your current password is not right.',
    password: 'New password',
    confirm: 'Type it again',
    confirmRequired: 'Type the new password again',
    mismatch: "The two passwords don't match",
    ruleLength: 'At least 8 characters',
    ruleNotEmail: 'Not your email address',
    ruleDifferent: 'Different from your current password',
    sameAsCurrent: "That's already your password. Choose a different one.",
    submit: 'Save and continue',
    submitting: 'Saving your password',
    show: 'Show password',
    hide: 'Hide password',
  },

  inactive: {
    title: 'Your account is switched off',
    body: "Your gym has switched this account off, or it is not set up yet. Ask your gym's owner to switch it back on.",
  },
} as const;
